import { motion } from "framer-motion";
import { Target, MessageSquare, BarChart3, Repeat } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: Target,
    title: "Choose a Scenario",
    description: "Pick from dozens of pre-built scenarios or create your own. Salary negotiation, cold outreach, performance review —",
    descriptionMobile: "you name it.",
  },
  {
    number: "02",
    icon: MessageSquare,
    title: "Start the Conversation",
    description: "Engage in a realistic dialogue with our AI. It adapts to your responses in real-time, creating authentic pressure and dynamics.",
  },
  {
    number: "03",
    icon: BarChart3,
    title: "Get Scored & Coached",
    description: "Receive instant performance scoring on confidence, persuasion, empathy, and strategy. Plus targeted coaching tips after each session.",
  },
  {
    number: "04",
    icon: Repeat,
    title: "Repeat & Improve",
    description: "Track your progress over time. The AI remembers your strengths and weaknesses, adjusting difficulty to push your growth edge.",
  },
];

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-[45px] md:py-[90px] stripe-bg relative">
      <div className="absolute inset-0 section-glow-left" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-20"
        >
          <div className="section-overline mb-4">How It Works</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">
            Four steps to{" "}
            <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">
              conversation mastery
            </span>
          </h2>
          <p className="text-base md:text-lg text-stripe-text-secondary max-w-md mx-auto">
            From beginner to expert negotiator — our AI guides you through a structured learning path.
          </p>
        </motion.div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-1/2 left-12 right-12 h-px bg-gradient-to-r from-stripe-border via-stripe-primary/20 to-stripe-border -translate-y-1/2" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="relative group"
              >
                <div className="stripe-card rounded-2xl p-8 text-center h-full group-hover:border-stripe-primary/20">
                  <div className="w-14 h-14 rounded-2xl bg-stripe-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-stripe-primary/15 group-hover:scale-105 transition-all duration-300">
                    <step.icon className="w-7 h-7 text-stripe-primary" />
                  </div>
                  <div className="font-heading font-bold text-xs text-stripe-primary mb-3 tracking-wider">
                    STEP {step.number}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-stripe-text-secondary leading-relaxed">
                    {step.description}
                    {step.descriptionMobile && (
                      <>
                        <br className="md:hidden" />
                        {step.descriptionMobile}
                      </>
                    )}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
